import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Database, Search, Download, RefreshCw, Eye, Trash2, ChevronLeft, ChevronRight, FileJson, FileSpreadsheet, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

// Human-friendly labels for the Mongo collection names
const COLLECTION_LABELS = {
  users: { ar: 'المستخدمون', en: 'Users', fr: 'Utilisateurs' },
  appointments: { ar: 'المواعيد', en: 'Appointments', fr: 'Rendez-vous' },
  call_queue: { ar: 'طابور المكالمات', en: 'Call Queue', fr: 'File d\'attente' },
  call_sessions: { ar: 'جلسات المكالمات', en: 'Call Sessions', fr: 'Sessions d\'appel' },
  medical_notes: { ar: 'الملاحظات الطبية', en: 'Medical Notes', fr: 'Notes médicales' },
  medical_files: { ar: 'الملفات الطبية', en: 'Medical Files', fr: 'Fichiers médicaux' },
  subscriptions: { ar: 'الاشتراكات', en: 'Subscriptions', fr: 'Abonnements' },
  activity_logs: { ar: 'سجل النشاط', en: 'Activity Logs', fr: 'Journaux' },
};

// Decide which columns to show in the table for each collection — keeps it readable.
const TABLE_COLUMNS = {
  users: ['full_name', 'email', 'phone_number', 'role', 'specialty', 'created_at'],
  appointments: ['patient_name', 'doctor_name', 'appointment_date', 'time_slot', 'status'],
  call_queue: ['patient_name', 'doctor_name', 'call_type', 'status', 'created_at'],
  call_sessions: ['patient_name', 'doctor_name', 'channel_name', 'call_type', 'created_at'],
  medical_notes: ['author_name', 'author_role', 'patient_id', 'notes', 'medications', 'created_at'],
  medical_files: ['original_filename', 'content_type', 'description', 'patient_id', 'created_at'],
  subscriptions: ['user_id', 'plan', 'status', 'expires_at'],
  activity_logs: ['action', 'user_role', 'details', 'timestamp'],
};

const formatCell = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') {
    if (value.length > 60) return value.slice(0, 57) + '…';
    return value;
  }
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === 'object') return '{…}';
  return String(value);
};

export const AdminDatabaseView = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState('users');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState({ rows: [], total: 0, limit: PAGE_SIZE, skip: 0 });
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [viewDoc, setViewDoc] = useState(null);

  // Fetch collection list once
  const refreshCollections = async () => {
    try {
      const r = await axios.get(`${API}/admin/db/collections`, { withCredentials: true });
      setCollections(r.data || []);
    } catch (e) {
      toast.error(t('فشل تحميل القائمة', 'Failed to load collections', 'Échec du chargement'));
    }
  };

  useEffect(() => { refreshCollections(); }, []);

  // Debounce search input — 300ms after the user stops typing
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Reset paging when collection or search changes
  useEffect(() => { setSkip(0); }, [active, debounced]);

  // Fetch rows whenever the active collection / search / paging changes
  useEffect(() => {
    let cancelled = false;
    const fetchRows = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ skip: String(skip), limit: String(PAGE_SIZE) });
        if (debounced.trim()) params.set('q', debounced.trim());
        const r = await axios.get(`${API}/admin/db/collections/${active}?${params.toString()}`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(t('فشل تحميل البيانات', 'Failed to load rows', 'Échec du chargement'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRows();
    return () => { cancelled = true; };
  }, [active, debounced, skip, t]);

  const handleExport = async (fmt) => {
    try {
      const r = await axios.get(`${API}/admin/db/collections/${active}/export/${fmt}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${active}_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('تم التصدير', `Exported ${active}`, `Exporté: ${active}`));
    } catch (e) {
      toast.error(t('فشل التصدير', 'Export failed', "Échec de l'export"));
    }
  };

  const handleDelete = async (rowId) => {
    if (!window.confirm(t('هل أنت متأكد من حذف هذا السجل؟', 'Delete this document?', 'Supprimer ce document ?'))) return;
    try {
      await axios.delete(`${API}/admin/db/collections/${active}/${rowId}`, { withCredentials: true });
      toast.success(t('تم الحذف', 'Deleted', 'Supprimé'));
      // Refresh current page
      setData({ ...data, rows: data.rows.filter(r => r._id !== rowId), total: Math.max(0, data.total - 1) });
      refreshCollections();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحذف', 'Delete failed', 'Échec de la suppression'));
    }
  };

  const columns = useMemo(() => TABLE_COLUMNS[active] || [], [active]);
  const total = data.total;
  const pageEnd = Math.min(skip + PAGE_SIZE, total);

  return (
    <div className={`space-y-4 ${isRTL ? 'rtl' : 'ltr'}`} data-testid="admin-db-view">
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        {/* Collections sidebar */}
        <Card className="p-3 h-fit" data-testid="db-collections-list">
          <div className="flex items-center gap-2 mb-3 px-2">
            <Database size={18} className="text-[#1565c0]" />
            <h3 className="font-bold text-[#0d47a1]">{t('المجموعات', 'Collections', 'Collections')}</h3>
            <button
              onClick={refreshCollections}
              className="ms-auto text-gray-400 hover:text-[#1565c0]"
              title={t('تحديث', 'Refresh', 'Actualiser')}
              data-testid="db-refresh-collections"
            >
              <RefreshCw size={14} />
            </button>
          </div>
          <ul className="space-y-1">
            {collections.map((c) => {
              const labelObj = COLLECTION_LABELS[c.name] || { ar: c.name, en: c.name, fr: c.name };
              const label = labelObj[language] || labelObj.en;
              const isActive = active === c.name;
              return (
                <li key={c.name}>
                  <button
                    onClick={() => setActive(c.name)}
                    data-testid={`db-collection-${c.name}`}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition
                      ${isActive ? 'bg-[#1565c0] text-white' : 'hover:bg-blue-50 text-gray-700'}`}
                  >
                    <span className="truncate">{label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>
                      {c.count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>

        {/* Main pane */}
        <Card className="p-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]" data-testid="db-search-wrap">
              <Search size={16} className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} text-gray-400`} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('ابحث في هذه المجموعة...', 'Search this collection...', 'Rechercher...')}
                className={isRTL ? 'pr-9' : 'pl-9'}
                data-testid="db-search-input"
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-xl" data-testid="db-export-button">
                  <Download size={14} className="me-2" />
                  {t('تصدير', 'Export', 'Exporter')}
                  <ChevronDown size={14} className="ms-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('json')} data-testid="db-export-json">
                  <FileJson size={14} className="me-2" /> JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="db-export-csv">
                  <FileSpreadsheet size={14} className="me-2" /> CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Status */}
          <div className="text-xs text-gray-500 mb-3" data-testid="db-status">
            {loading
              ? t('جاري التحميل...', 'Loading...', 'Chargement...')
              : total === 0
                ? t('لا توجد سجلات', 'No records', 'Aucun enregistrement')
                : `${skip + 1}–${pageEnd} ${t('من', 'of', 'sur')} ${total}`
            }
          </div>

          {/* Table */}
          <div className="overflow-x-auto border rounded-xl">
            <table className="w-full text-sm" data-testid="db-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {columns.map((c) => (
                    <th key={c} className={`px-3 py-2 font-semibold text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>{c}</th>
                  ))}
                  <th className={`px-3 py-2 ${isRTL ? 'text-right' : 'text-left'} w-24`}>{t('إجراءات', 'Actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row._id} className="border-b last:border-0 hover:bg-blue-50/50" data-testid={`db-row-${row._id}`}>
                    {columns.map((c) => (
                      <td key={c} className="px-3 py-2 align-top text-gray-700" title={typeof row[c] === 'string' ? row[c] : ''}>
                        {formatCell(row[c])}
                      </td>
                    ))}
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        onClick={() => setViewDoc(row)}
                        className="text-[#1565c0] hover:text-[#0d47a1] p-1"
                        title={t('عرض', 'View', 'Voir')}
                        data-testid={`db-view-${row._id}`}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(row._id)}
                        className="text-red-500 hover:text-red-600 p-1 ms-1"
                        title={t('حذف', 'Delete', 'Supprimer')}
                        data-testid={`db-delete-${row._id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && data.rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 1} className="px-3 py-6 text-center text-gray-400">
                      {t('لا توجد بيانات', 'No data', 'Aucune donnée')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pager */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3">
              <Button
                variant="outline"
                size="sm"
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                data-testid="db-prev-page"
              >
                {isRTL ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                {t('السابق', 'Prev', 'Préc.')}
              </Button>
              <span className="text-xs text-gray-500">
                {t('صفحة', 'Page', 'Page')} {Math.floor(skip / PAGE_SIZE) + 1} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={skip + PAGE_SIZE >= total}
                onClick={() => setSkip(skip + PAGE_SIZE)}
                data-testid="db-next-page"
              >
                {t('التالي', 'Next', 'Suiv.')}
                {isRTL ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Document detail dialog */}
      <Dialog open={!!viewDoc} onOpenChange={(open) => !open && setViewDoc(null)}>
        <DialogContent className={`max-w-2xl max-h-[80vh] overflow-hidden flex flex-col ${isRTL ? 'rtl' : 'ltr'}`} data-testid="db-doc-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0d47a1]">
              {t('تفاصيل السجل', 'Document Details', 'Détails')}
              <span className="ms-2 text-xs text-gray-500 font-mono">{viewDoc?._id}</span>
            </DialogTitle>
          </DialogHeader>
          <pre className="bg-gray-900 text-green-300 text-xs p-4 rounded-lg overflow-auto font-mono leading-relaxed" style={{ direction: 'ltr', textAlign: 'left' }}>
            {viewDoc ? JSON.stringify(viewDoc, null, 2) : ''}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDatabaseView;
